/**
 * 资产素材库文件管理器
 * 负责管理资产素材库文件的业务操作
 * @author Joey
 */

import { useToast } from "@/hooks/use-toast";
import { canUseWindow } from "@/utils/canuseWindow";
import { useEffect, useRef, useState } from "react";
import { jsonStore } from "@/manager/cache/localdb";
import { materialItemUpload, MaterialItemUploadData, MaterialItemUploadRequest, TagData } from "@/services/material-item-upload";
import { useParams } from "react-router-dom";
import { MaterialFolderItemData, userMaterialGetFolderItems } from "@/services/user-material-get-folder-items";
import { materialItemDelete } from "@/services/material-item-delete";
import { useUploadController } from "@/hooks/use-upload-controller";
import { isAxiosError } from "axios";
import { assetsMaterialHooks } from "./assets-material-manager";
import { useDebounce } from "@/hooks/use-debounce";
import { MaterialItemData, userMaterialGetAllFolderItems } from "@/services/user-material-get-all-folder-items";

// 缓存数据结构
interface CacheData {
  materialItems: MaterialItemData[];
}

type MaterialItemsChangeEventDetail = {
  materialItems: MaterialItemData[];
};
type MaterialItemsChangeEvent = CustomEvent<MaterialItemsChangeEventDetail>;

class AssetsMaterialItemLocal {
  constructor(private storageKey: string) { }
  async get(id: string): Promise<CacheData | null> {
    return await jsonStore.get<CacheData>(`${this.storageKey}:${id}`);
  }
  async set(id: string, data: CacheData): Promise<void> {
    await jsonStore.set(`${this.storageKey}:${id}`, data);
  }
  async clear(): Promise<void> {
    await jsonStore.set(this.storageKey, null);
  }
}

class AssetsMaterialItemRemote {
  constructor(private cache: AssetsMaterialItemLocal) { }
  async getCached(id: string) {
    return await this.cache.get(id);
  }
  async fetchMaterialItems(folderId: number) {
    const data = await userMaterialGetAllFolderItems({ parentFolderId: folderId });
    await this.cache.set(folderId.toString(), { materialItems: data.itemList });
    return data.itemList || [];
  }
  async uploadMaterialItems({ file, folderId, tagId, onProgress, signal }: MaterialItemUploadRequest): Promise<MaterialItemUploadData> {
    return await materialItemUpload({
      file,
      folderId,
      tagId,
      onProgress,
      signal,
    });
  }
  async deleteMaterialItems(ids: number[]) {
    return await materialItemDelete(ids);
  }
}

export class AssetsMaterialItemManager {
  private static instance: AssetsMaterialItemManager;
  private readonly STORAGE_KEY = "MIUTA_ASSETS_MATERIAL_ITEM";
  private materialItems: MaterialItemData[] = [];
  private cache: AssetsMaterialItemLocal;
  private api: AssetsMaterialItemRemote;

  private constructor() {
    this.cache = new AssetsMaterialItemLocal(this.STORAGE_KEY);
    this.api = new AssetsMaterialItemRemote(this.cache);
  }

  public static getInstance(): AssetsMaterialItemManager {
    if (!AssetsMaterialItemManager.instance) {
      AssetsMaterialItemManager.instance = new AssetsMaterialItemManager();
    }
    return AssetsMaterialItemManager.instance;
  }

  // 初始化
  public async init(folderId: number) {
    try {
      const cached = await this.api.getCached(folderId.toString());
      // if (cached && cached.materialItems) {
      //   this.setMaterialItems(folderId.toString(), cached.materialItems);
      //   this.fetchMaterialItem(folderId);
      //   return;
      // }
    } catch (error) {
      console.error("get cache data failed", error);
      this.setMaterialItems(folderId.toString(), []);
    }
    await this.fetchMaterialItem(folderId);
  }

  // 请求素材库数据
  public async fetchMaterialItem(folderId: number) {
    try {
      const items = await this.api.fetchMaterialItems(folderId);
      this.setMaterialItems(folderId.toString(), items);
    } catch (error) {
      console.error("Failed to fetch assets material item list:", error);
      this.dispatchMaterialItemsChange();
      throw error;
    }
  }

  // 上传素材
  public async materialItemUpload({ file, folderId, tagId, onProgress, signal }: MaterialItemUploadRequest) {
    try {
      return await this.api.uploadMaterialItems({ file, folderId, tagId, onProgress, signal });
    } catch (error) {
      console.error("Failed to upload assets material item:", error);
      throw error;
    }
  }

  // 删除
  public async materialItemDelete(folderId: string, id: number[]) {
    const originMaterials = [...this.materialItems];
    try {
      // 乐观更新
      const materials = this.getMaterialItems().filter((item) => !id.includes(item.id));
      this.setMaterialItems(folderId, materials);

      await this.api.deleteMaterialItems(id);
    } catch (error) {
      this.setMaterialItems(folderId, originMaterials);
      throw error;
    }
  }

  // ==================== 读取写入操作 ====================
  public getMaterialItems() {
    return this.materialItems;
  }
  public setMaterialItems(folderId: string, list: MaterialItemData[]) {
    this.materialItems = list;
    this.dispatchMaterialItemsChange();
    this.cache.set(folderId, { materialItems: this.materialItems });
  }

  // ==================== 订阅通知操作 ====================
  // 监听列表变化事件
  private dispatchMaterialItemsChange(): void {
    if (!canUseWindow()) return;
    const event = new CustomEvent<MaterialItemsChangeEventDetail>(`${this.STORAGE_KEY}:list-change`, {
      detail: { materialItems: this.materialItems }
    });
    window.dispatchEvent(event);
  }
  public onMaterialItemsChange(listener: (e: MaterialItemsChangeEvent) => void) {
    if (!canUseWindow()) return;
    const eventName = `${this.STORAGE_KEY}:list-change`;
    window.addEventListener(eventName, listener);
    return () => window.removeEventListener(eventName, listener);
  }
}

// ============= Hooks =============
const useMaterialItemsData = () => {
  const toast = useToast();
  const params = useParams();

  const [loading, setLoading] = useState(false);
  const [materialItems, setMaterialItems] = useState(assetsMaterialItemManager.getMaterialItems());
  const { materialFolders } = assetsMaterialHooks.useMaterialFoldersData();
  const initRef = useRef(false);

  const fetchMaterialItems = async () => {
    try {
      if (!initRef.current) {
        setLoading(true);
      }
      await assetsMaterialItemManager.init(Number(params.id));
      initRef.current = true;
    } catch (error) {
      console.log(error)
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  }

  const currentFolder = materialFolders.find((item) => item.id === Number(params.id));

  // 防抖请求素材库数据
  const debouncedFetchMaterialItems = useDebounce(fetchMaterialItems, 400, {
    immediately: true,
  });

  useEffect(() => {
    debouncedFetchMaterialItems();
  }, [params.id]);

  useEffect(() => {
    const unsubscribe = assetsMaterialItemManager.onMaterialItemsChange((e) => {
      setMaterialItems(e.detail.materialItems)
    })
    return unsubscribe
  }, [])

  return {
    materialItems,
    loading,
    fetchMaterialItems,
    currentFolder,
  }
}

// 删除
const useMaterialItemDelete = () => {
  const { showToastByCode } = useToast();
  const [loading, setLoading] = useState(false);
  const params = useParams();

  const deleteMaterialItem = async (id: number[]) => {
    try {
      setLoading(true);
      await assetsMaterialItemManager.materialItemDelete(params.id, id);
    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteMaterialItem,
    loading,
  };
};

// 上传素材
const useMaterialItemUpload = () => {
  const [loading, setLoading] = useState(false);
  const { showToastByCode } = useToast();
  const params = useParams();
  const { addUploadTasks } = useUploadController();

  const uploadMaterialItem = async (files: File[], tagIds: number[]) => {
    try {
      setLoading(true);

      addUploadTasks(files, async (file, onProgress, signal) => {
        try {
          return await assetsMaterialItemManager.materialItemUpload({
            file,
            folderId: Number(params.id),
            tagId: tagIds.join(','),
            onProgress,
            signal,
          });
        } catch (error) {
          if (isAxiosError(error) && error.code === 'ERR_CANCELED') throw error;
          console.error(error)
          showToastByCode(error);
          throw new Error(error)
        }
      }, {
        mode: 'parallel',
        concurrency: 5,
        onTaskComplete(task, result) {
          // 成功后更新列表
          if (result?.successAddItem?.length > 0) {
            result?.successAddItem.forEach((item) => {
              item.tags = item.tags || tagIds.map(id => ({ id, tagName: '' }))
            })
            assetsMaterialItemManager.setMaterialItems(params.id, [
              ...result.successAddItem,
              ...assetsMaterialItemManager.getMaterialItems(),
            ]);
          }
        },
      })

    } catch (error) {
      showToastByCode(error);
      throw new Error(error)
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    uploadMaterialItem,
  };
};

export const assetsMaterialItemHooks = {
  useMaterialItemsData,
  useMaterialItemDelete,
  useMaterialItemUpload,
}

// 导出单例实例
export const assetsMaterialItemManager = AssetsMaterialItemManager.getInstance();
